// The room's seat list. It is positional: the top names play (as many as the game seats) and everyone
// below the line watches. The host, marked with a crown, can sit anywhere in it, playing or watching;
// they rearrange it by dragging a name or with the small arrows, and hand the crown over (after a yes).
import React from 'react';
import type { ParticipantView, RoomView } from '../../types/room';

const DOTS = ['#8fb8dd', '#ec9a85', '#9fd3a6', '#c7a6e8', '#f2c46b', '#7fd1c7', '#e8a0c8', '#b8c47a'];

interface Props {
  room: RoomView;
  myId: string;
  isLeader: boolean;
  seatName: (i: number, p: ParticipantView) => string;   // "Blue", "Red", "Seat 3"
  arrange: (order: string[], watching: string[]) => void;
  removeParticipant: (id: string) => void;
  setSpectator: (spectate: boolean) => void;
  transferLeader: (id: string) => void;
  locked?: boolean;                          // a league game: the league decides who plays
}

// Players in seat order, then the people watching.
export function seatOrder(room: RoomView): { list: ParticipantView[]; line: number } {
  const players = room.participants.filter(p => p.slot !== null).sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0));
  const watching = room.participants.filter(p => p.slot === null);
  return { list: [...players, ...watching], line: players.length };
}

export const Crown = ({ filled = true, size = 16, label }: { filled?: boolean; size?: number; label?: string }) => (
  <svg role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true} width={size} height={size} viewBox="0 0 24 24"
    fill={filled ? 'var(--accent)' : 'none'} stroke={filled ? 'var(--accent)' : 'currentColor'} strokeWidth={filled ? 1.5 : 1.8} strokeLinejoin="round" style={{ flex: 'none', display: 'inline-block', verticalAlign: '-2px' }}>
    <path d="M3 18h18l-1.5-10-4.5 4-3-7-3 7-4.5-4z" />
  </svg>
);

const Arrow = ({ up }: { up: boolean }) => (
  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d={up ? 'M6 15l6-6 6 6' : 'M6 9l6 6 6-6'} /></svg>
);

export default function SeatList({ room, myId, isLeader: hosting, seatName, arrange, removeParticipant, setSpectator, transferLeader, locked = false }: Props) {
  const isLeader = hosting && !locked;
  const order = seatOrder(room);
  const line = order.line;
  // A league game lists Blue before Red, whoever sits where.
  const blueFirst = (p: ParticipantView) => (seatName(p.slot ?? 0, p) === 'Blue' ? 0 : 1);
  const list = locked ? [...order.list.slice(0, line).sort((a, b) => blueFirst(a) - blueFirst(b)), ...order.list.slice(line)] : order.list;
  const seats = room.seats;
  // While dragging: the order as it is now, and which row is being held.
  const [drag, setDrag] = React.useState<{ order: ParticipantView[]; at: number } | null>(null);
  // Someone the host is handing the crown to, waiting for a yes.
  const [crownTo, setCrownTo] = React.useState<ParticipantView | null>(null);
  const shown = drag?.order ?? list;
  const shownLine = line;

  // Bots can't watch.
  const valid = (order: ParticipantView[], l: number) => order.slice(l).every(p => p.type !== 'bot');
  const commit = (order: ParticipantView[], l: number) => {
    setDrag(null);
    if (valid(order, l)) send(order, l);
  };
  // When every seat is taken, people below the line watch only for want of a seat: they aren't marked
  // as watching, so they play when the room switches to a game with more seats.
  const send = (order: ParticipantView[], l: number) =>
    arrange(order.map(p => p.id), seats != null && l >= seats ? [] : order.slice(l).filter(p => p.type !== 'bot').map(p => p.id));

  const swap = (i: number, j: number) => { const o = list.slice(); [o[i], o[j]] = [o[j], o[i]]; return o; };
  const full = seats != null && line >= seats;
  // Up: the first watcher takes an open seat; otherwise swap with the name above.
  const up = (i: number): [ParticipantView[], number] | null => {
    if (i === 0) return null;
    if (i === line && !full) return [list, line + 1];
    return [swap(i, i - 1), line];
  };
  // Down: the last player watches if no one is waiting for a seat; otherwise swap with the name below.
  const down = (i: number): [ParticipantView[], number] | null => {
    if (i === line - 1 && (!full || i === list.length - 1)) return [list, line - 1];
    if (i >= list.length - 1) return null;
    return [swap(i, i + 1), line];
  };
  const ok = (m: [ParticipantView[], number] | null) => !!m && valid(m[0], m[1]);

  const finish = () => { if (drag) commit(drag.order, line); };
  const me = room.participants.find(p => p.id === myId);

  return (
    <>
      <ol aria-label="Seats: the players, then the people watching" onPointerUp={finish} onPointerLeave={finish}
        style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {shown.map((p, i) => {
          const self = p.id === myId;
          const playing = i < shownLine;
          const held = drag?.at === i;
          const u = up(i), d = down(i);
          const side = playing ? seatName(i, p) : '';
          const dot = side === 'Blue' ? '#8fb8dd' : side === 'Red' ? '#ec9a85' : DOTS[i % DOTS.length];
          return (
            <React.Fragment key={p.id}>
              {i === shownLine && (
                <li aria-hidden="true" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--muted)', fontSize: 12, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', margin: '6px 0 0' }}>
                  <span style={{ flex: 1, borderTop: '1px dashed var(--line)' }} />Watching<span style={{ flex: 1, borderTop: '1px dashed var(--line)' }} />
                </li>
              )}
              <li
                onPointerDown={e => { if (!isLeader || (e.target as HTMLElement).closest('button')) return; setDrag({ order: list.slice(), at: i }); }}
                onPointerEnter={() => {
                  if (!drag || drag.at === i) return;
                  const o = drag.order.slice(); const [x] = o.splice(drag.at, 1); o.splice(i, 0, x);
                  setDrag({ order: o, at: i });
                }}
                onDragStart={e => e.preventDefault()}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, borderRadius: 10, padding: '8px 8px', userSelect: 'none', touchAction: isLeader ? 'none' : 'auto',
                  cursor: isLeader ? (held ? 'grabbing' : 'grab') : 'default',
                  ...(playing ? { background: 'var(--bg)', border: '1px solid var(--panel-line)' } : { border: '1px dashed var(--line)', color: '#d6d4df' }),
                  ...(held ? { boxShadow: '0 6px 18px rgba(0,0,0,.45)', outline: '2px solid var(--accent)' } : {}),
                }}>
                {isLeader && <span aria-hidden="true" style={{ color: 'var(--faint)', fontSize: 14, lineHeight: 1, width: 14, flex: 'none', overflow: 'hidden', letterSpacing: -3 }}>⋮⋮</span>}
                <span style={{ flex: 'none', minWidth: 42, fontSize: 12, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: playing ? dot : 'var(--faint)' }}>
                  {side}
                </span>
                <span aria-hidden="true" style={{ width: 26, height: 26, borderRadius: '50%', flex: 'none', background: playing ? dot : '#6f6d7d', color: '#24262e', fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {p.name[0]?.toUpperCase()}
                </span>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                  <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</strong>
                  {p.isLeader && <Crown label="Host" />}
                  <span className="site-muted" style={{ fontSize: 13 }}>
                    {self && ' (you)'}{p.type === 'bot' && ' · bot'}
                    {!p.isConnected && p.type !== 'bot' && <span style={{ color: 'var(--danger)' }}> · away</span>}
                  </span>
                </span>
                {isLeader && !self && p.type !== 'bot' && p.isConnected && (
                  <button type="button" className="site-icon-btn" style={{ width: 28 }} aria-label={`Make ${p.name} the host`} title={`Make ${p.name} the host`} onClick={() => setCrownTo(p)}><Crown filled={false} /></button>
                )}
                {isLeader && (
                  <span style={{ display: 'flex', flexDirection: 'column' }}>
                    <button type="button" className="site-icon-btn" style={{ width: 22, height: 18 }} aria-label={`Move ${p.name} up`} disabled={!ok(u)} onClick={() => u && commit(u[0], u[1])}><Arrow up /></button>
                    <button type="button" className="site-icon-btn" style={{ width: 22, height: 18 }} aria-label={`Move ${p.name} down`} disabled={!ok(d)} onClick={() => d && commit(d[0], d[1])}><Arrow up={false} /></button>
                  </span>
                )}
                {isLeader && !self && (
                  <button type="button" className="site-icon-btn" style={{ width: 28 }} aria-label={`Remove ${p.name}`} title={`Remove ${p.name} from the room`}
                    onClick={() => { if (p.type === 'bot' || window.confirm(`Remove ${p.name} from the room?`)) removeParticipant(p.id); }}>✕</button>
                )}
              </li>
            </React.Fragment>
          );
        })}
      </ol>
      {me && me.type !== 'bot' && !isLeader && !locked && (
        <div>
          {me.slot !== null
            ? <button type="button" className="site-btn site-btn-small" onClick={() => setSpectator(true)}>Watch instead</button>
            : me.type === 'spectator'
              ? <button type="button" className="site-btn site-btn-small" onClick={() => setSpectator(false)}>I'd like to play</button>
              : <span className="site-muted site-small">Every seat is taken: you'll play when one opens, or when the host moves you up.</span>}
        </div>
      )}
      {crownTo && (
        <div className="site-dialog-back" role="dialog" aria-modal="true" aria-labelledby="hostq">
          <div className="site-dialog">
            <h2 id="hostq">Make {crownTo.name} the host?</h2>
            <p style={{ color: '#e8dfd2' }}>{crownTo.name} gets the crown: choosing the game and its settings, arranging seats and starting games. Nobody's seat changes. {crownTo.name} can hand the crown back the same way.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button type="button" className="site-btn" onClick={() => setCrownTo(null)}>Cancel</button>
              <button type="button" className="site-btn site-btn-primary" onClick={() => { transferLeader(crownTo.id); setCrownTo(null); }}>Make {crownTo.name} host</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
